import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Key, Loader as Loader2, Lock, Mail, UserPlus, X } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { callSiteApi } from '../utils';
import { Portal } from '../Portal';
import type { Site } from '../types';

interface CreateUserModalProps {
  site: Site;
  onClose: () => void;
  onCreated: () => void;
}

const roles = [
  { label: 'Administrators', value: 'administrator' },
  { label: 'Editors', value: 'editor' },
  { label: 'Authors', value: 'author' },
  { label: 'Contributors', value: 'contributor' },
  { label: 'Subscribers', value: 'subscriber' },
];

export const CreateUserModal: React.FC<CreateUserModalProps> = ({ site, onClose, onCreated }) => {
  const { toast } = useToast();
  const [isCreating, setIsCreating] = useState(false);
  const [createData, setCreateData] = useState({
    username: '',
    email: '',
    password: '',
    role: 'subscriber',
    first_name: '',
    last_name: ''
  });

  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setIsCreating(true);
      await callSiteApi(site.url, site.publicKey, site.secretKey, 'users', 'POST', JSON.stringify(createData));
      onCreated();
    } catch (e: any) {
      toast({
        title: "Creation Failed",
        description: e.message || "Could not create user.",
        variant: "destructive",
      });
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <Portal>
      <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="absolute inset-0 bg-black/60 backdrop-blur-sm"
          onClick={() => !isCreating && onClose()}
        />
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          className="relative w-full max-w-lg bg-card border border-border rounded-2xl shadow-2xl overflow-hidden z-50 flex flex-col max-h-[90vh]"
        >
          <div className="px-6 py-4 border-b border-border flex items-center justify-between bg-muted/30">
            <h3 className="font-bold text-foreground">Create New WordPress User</h3>
            <button
              onClick={onClose}
              disabled={isCreating}
              className="p-2 hover:bg-muted rounded-full transition-colors text-muted-foreground"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <form onSubmit={handleCreateUser} className="flex-1 overflow-y-auto p-6 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <label className="text-xs font-bold text-muted-foreground uppercase tracking-wider ml-1">Username</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <input
                    required
                    type="text"
                    value={createData.username}
                    onChange={(e) => setCreateData({ ...createData, username: e.target.value })}
                    className="w-full pl-10 pr-4 py-2 bg-muted/40 border border-border rounded-xl text-sm focus:ring-2 focus:ring-workspace-accent/30 focus:border-workspace-accent outline-none"
                  />
                </div>
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-bold text-muted-foreground uppercase tracking-wider ml-1">Password</label>
                <div className="relative">
                  <Key className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <input
                    required
                    type="password"
                    value={createData.password}
                    onChange={(e) => setCreateData({ ...createData, password: e.target.value })}
                    className="w-full pl-10 pr-4 py-2 bg-muted/40 border border-border rounded-xl text-sm focus:ring-2 focus:ring-workspace-accent/30 focus:border-workspace-accent outline-none"
                  />
                </div>
              </div>
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-bold text-muted-foreground uppercase tracking-wider ml-1">Email Address</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <input
                  required
                  type="email"
                  value={createData.email}
                  onChange={(e) => setCreateData({ ...createData, email: e.target.value })}
                  className="w-full pl-10 pr-4 py-2 bg-muted/40 border border-border rounded-xl text-sm focus:ring-2 focus:ring-workspace-accent/30 focus:border-workspace-accent outline-none"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <label className="text-xs font-bold text-muted-foreground uppercase tracking-wider ml-1">First Name</label>
                <input
                  type="text"
                  value={createData.first_name}
                  onChange={(e) => setCreateData({ ...createData, first_name: e.target.value })}
                  className="w-full px-4 py-2 bg-muted/40 border border-border rounded-xl text-sm focus:ring-2 focus:ring-workspace-accent/30 focus:border-workspace-accent outline-none"
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-bold text-muted-foreground uppercase tracking-wider ml-1">Last Name</label>
                <input
                  type="text"
                  value={createData.last_name}
                  onChange={(e) => setCreateData({ ...createData, last_name: e.target.value })}
                  className="w-full px-4 py-2 bg-muted/40 border border-border rounded-xl text-sm focus:ring-2 focus:ring-workspace-accent/30 focus:border-workspace-accent outline-none"
                />
              </div>
            </div>

            <div className="space-y-1.5 pb-2">
              <label className="text-xs font-bold text-muted-foreground uppercase tracking-wider ml-1">Role</label>
              <select
                value={createData.role}
                onChange={(e) => setCreateData({ ...createData, role: e.target.value })}
                className="w-full px-4 py-2 bg-muted/40 border border-border rounded-xl text-sm focus:ring-2 focus:ring-workspace-accent/30 focus:border-workspace-accent outline-none"
              >
                {roles.map(r => (
                  <option key={r.value} value={r.value}>{r.label}</option>
                ))}
              </select>
            </div>

            <div className="flex gap-3 pt-4 sticky bottom-0 bg-card rounded-b-2xl">
              <button
                type="button"
                onClick={onClose}
                disabled={isCreating}
                className="flex-1 px-4 h-11 border border-border rounded-xl text-sm font-bold hover:bg-muted transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isCreating}
                className="flex-1 px-4 h-11 bg-workspace-accent hover:bg-workspace-accent-hover text-white rounded-xl text-sm font-bold shadow-lg shadow-workspace-accent/20 transition-all flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {isCreating ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> Creating...</>
                ) : (
                  <><UserPlus className="w-4 h-4" /> Create User</>
                )}
              </button>
            </div>
          </form>
        </motion.div>
      </div>
    </Portal>
  );
};
